import { supabase } from "../lib/supabase";

const DEFAULT_SEND_TIME = "07:00";
const DEFAULT_EVENING_SEND_TIME = "20:00";
const DEFAULT_TIMEZONE = "Europe/Zurich";

function deviceTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

/** Normalize Postgres time ("07:00:00") to "HH:MM" for <input type="time">. */
function toHhMm(value, fallback = DEFAULT_SEND_TIME) {
  const match = /^(\d{1,2}):(\d{2})/.exec(String(value || ""));
  if (!match) return fallback;
  return `${match[1].padStart(2, "0")}:${match[2]}`;
}

/**
 * Current user's daily briefing settings (defaults when no row exists). Covers
 * both touchpoints: Morning Briefing and Evening Decompression.
 * @returns {Promise<{ enabled: boolean, sendTime: string, eveningEnabled: boolean, eveningSendTime: string }>}
 */
export async function fetchBriefingSettings() {
  const { data, error } = await supabase
    .from("briefing_settings")
    .select("enabled, send_time, evening_enabled, evening_send_time")
    .maybeSingle();
  if (error) throw error;
  if (!data) {
    return {
      enabled: false,
      sendTime: DEFAULT_SEND_TIME,
      eveningEnabled: false,
      eveningSendTime: DEFAULT_EVENING_SEND_TIME,
    };
  }
  return {
    enabled: Boolean(data.enabled),
    sendTime: toHhMm(data.send_time),
    eveningEnabled: Boolean(data.evening_enabled),
    eveningSendTime: toHhMm(data.evening_send_time, DEFAULT_EVENING_SEND_TIME),
  };
}

/** Upsert only the given columns; unspecified touchpoint settings are preserved. */
async function upsertSettings(patch) {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();
  if (userError || !user) {
    throw new Error("Bitte melde dich an.");
  }

  const { error } = await supabase.from("briefing_settings").upsert(
    {
      user_id: user.id,
      timezone: deviceTimezone(),
      updated_at: new Date().toISOString(),
      ...patch,
    },
    { onConflict: "user_id" }
  );
  if (error) throw error;
}

/**
 * Upsert the current user's Morning Briefing settings (RLS: own row only).
 * @param {{ enabled: boolean, sendTime: string }} params
 */
export async function saveBriefingSettings({ enabled, sendTime }) {
  await upsertSettings({
    enabled: Boolean(enabled),
    send_time: toHhMm(sendTime),
  });
}

/**
 * Upsert the current user's Evening Decompression settings (RLS: own row only).
 * @param {{ enabled: boolean, sendTime: string }} params
 */
export async function saveEveningSettings({ enabled, sendTime }) {
  await upsertSettings({
    evening_enabled: Boolean(enabled),
    evening_send_time: toHhMm(sendTime, DEFAULT_EVENING_SEND_TIME),
  });
}
